import React, { useContext, useMemo, useState, useEffect, useRef } from "react";
import { FormContext, FormContextProvider } from "@sw-sw/lib-form";
import FormModal from "../Shared/form/modal/FormModal";
import { FormSchemaFields } from "@sw-sw/lib-form";
import { UIControlType } from "@sw-sw/lib-form-control-types";
import { useInspectionTypesByTemplate } from "../../hooks/inspectionTypes";
import Loading from "../Shared/ResourceIndex/Loading";
import ProjectContext from "../../contexts/ProjectContext";
import { getDateOnly } from "../../utils/date";

const InspectionTypeSelectionForm = ({ inspectionTypes, onChange, value, isMulti }) => {
  const schema = useMemo(() => {
    return {
      inspection_types: {
        controlType: UIControlType.customTagSelect,
        isMulti: isMulti,
        placeholder: "Choose one or more inspection types",
        options: Array.isArray(inspectionTypes) ? inspectionTypes.map(type => ({
          label: type,
          value: type
        })) : [],
        required: true,
      },
    };
  }, [inspectionTypes, isMulti]);

  return (
    <FormSchemaFields
      schema={schema}
      formData={value}
      onChange={onChange}
    />
  );
};

const ConfirmationModal = ({ show, hideModal, onConfirm, existingInspections }) => {
  if (!show) return null;

  return (
    <FormModal
      modalProps={{
        title: "Inspection Already Exists",
        submitBtnText: "Create Anyway",
        classes: "inspection-confirmation-modal",
      }}
      onCancel={hideModal}
      onSubmit={onConfirm}
    >
      <div style={{ marginBottom: "1rem" }}>
        <p>There is already an inspection created for today:</p>
        <ul style={{ marginTop: "0.5rem", marginBottom: "1rem" }}>
          {existingInspections.map((inspection, index) => (
            <li key={index}>
              Inspection #{inspection.inspectionNumber} - {inspection.inspection_types?.map(type => type.type || type).join(", ")}
            </li>
          ))}
        </ul>
        <p>Are you sure you want to create another inspection for today?</p>
      </div>
    </FormModal>
  );
};

const InspectionTypeSelectionModalUI = ({ hideModal, onSubmit, inspectionTypes, isMulti }) => {
  const formContext = useContext(FormContext);
  const { inspection_types } = formContext.value;
  const { inspections } = useContext(ProjectContext);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [selectedTypes, setSelectedTypes] = useState([]);
  const isMountedRef = useRef(true);

  // Cleanup function to prevent memory leaks
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Safe state setter that checks if component is still mounted
  const safeSetState = (setter, value) => {
    if (isMountedRef.current) {
      setter(value);
    }
  };

  // Check for existing inspections created today
  const checkForSameDayInspections = (typesToCreate) => {
    const today = getDateOnly(new Date());
    const existingTodayInspections = inspections?.filter(inspection => 
      inspection.created_date === today
    ) || [];

    if (existingTodayInspections.length > 0) {
      safeSetState(setSelectedTypes, typesToCreate);
      safeSetState(setShowConfirmation, true);
      return true;
    }
    return false;
  };

  const handleSubmit = () => {
    if (!inspection_types || inspection_types.length === 0) {
      return;
    }
    
    const typesToCreate = inspection_types.map(type => type.value);
    
    // Check if there are existing inspections for today
    if (!checkForSameDayInspections(typesToCreate)) {
      // No existing inspections, proceed normally
      onSubmit(typesToCreate);
      hideModal();
    }
  };

  const handleConfirmCreate = () => {
    onSubmit(selectedTypes);
    safeSetState(setShowConfirmation, false);
    safeSetState(setSelectedTypes, []);
    hideModal(); // Close the main modal after confirming
  };

  const handleCancelConfirmation = () => {
    safeSetState(setShowConfirmation, false);
    safeSetState(setSelectedTypes, []);
    hideModal(); // Close the main modal after canceling
  };

  const isSubmitDisabled = !inspection_types || inspection_types.length === 0;
  const today = getDateOnly(new Date());
  const existingTodayInspections = inspections?.filter(inspection => 
    inspection.created_date === today
  ) || [];

  return (
    <>
      <FormModal
        modalProps={{
          title: "New Inspection",
          submitBtnText: "Create Inspection",
          classes: "inspection-type-selection-modal",
        }}
        onCancel={hideModal}
        onSubmit={handleSubmit}
        submitDisabled={isSubmitDisabled}
      >
        <div style={{ marginBottom: "1rem" }}>
          <p>Select the type(s) of inspection you want to create:</p>
        </div>
        <InspectionTypeSelectionForm
          inspectionTypes={inspectionTypes}
          onChange={formContext.set}
          value={formContext.value}
          isMulti={isMulti}
        />
      </FormModal>
      
      <ConfirmationModal
        show={showConfirmation}
        hideModal={handleCancelConfirmation}
        onConfirm={handleConfirmCreate}
        existingInspections={existingTodayInspections}
      />
    </>
  );
};

const InspectionTypeSelectionModal = ({ show, hideModal, onSubmit }) => {
  const { template } = useContext(ProjectContext);

  // Always call the hook, but pass null when template is not available
  const hookResult = useInspectionTypesByTemplate(template?.id || null, show);
  
  // Use hook result if template exists, otherwise use fallback data
  const { data, isLoading, error } = template 
    ? hookResult 
    : { data: {inspectionTypes:['Post Storm','Routine']}, isLoading: false, error: null };

  // Set isMulti to false if template.isUserTemplate is false (system template)
  const isMulti = template?.isUserTemplate !== false;

  if (!show) {
    return null;
  }

  if (isLoading) {
    return (
      <FormModal
        modalProps={{
          title: "New Inspection",
          classes: "inspection-type-selection-modal",
        }}
        onCancel={hideModal}
        onSubmit={() => {}}
        submitDisabled={true}
      >
        <Loading what="Inspection Types" />
      </FormModal>
    );
  }

  if (error) {
    return (
      <FormModal
        modalProps={{
          title: "New Inspection",
          classes: "inspection-type-selection-modal",
        }}
        onCancel={hideModal}
        onSubmit={() => {}}
        submitDisabled={true}
      >
        <div>Error loading inspection types. Please try again.</div>
      </FormModal>
    );
  }

  return (
    <FormContextProvider initialValue={{ inspection_types: [] }}>
      <InspectionTypeSelectionModalUI
        hideModal={hideModal}
        onSubmit={onSubmit}
        inspectionTypes={data?.inspectionTypes}
        isMulti={isMulti}
      />
    </FormContextProvider>
  );
};

export default InspectionTypeSelectionModal;